import { useState } from 'react';
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { deriveAccess, normalizeMembership } from "../services/membershipService";
import { setActiveTenantSite } from "../services/activeTenant";
// Where each role lands after a successful login.
import { roleHome } from "../utils/roleHome";
import { UI_REVAMP_ENABLED } from "../config/featureFlags";
import {
  Mail,
  Lock,
  ShieldCheck,
  Loader2,
  ArrowRight,
  AlertCircle,
  Sparkles,
} from "lucide-react";


const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  // Password-rotation state, populated after the password step.
  const [pwdExpired, setPwdExpired] = useState(false);
  const [pwdWarnDays, setPwdWarnDays] = useState(null); // days left in 83–89 window
  const currentYear = new Date().getFullYear();

  const handleLogin = async (e) => {
  e.preventDefault();
  setError("");
  setLoading(true);

  try {
    // Step 1: Authenticate
    const cred = await signInWithEmailAndPassword(auth, email, password);

    // Fire-and-forget success audit. Non-blocking by design: this is NOT a
    // sign-in blocking function (those fail-closed and locked users out), so a
    // failure here can never affect login. The server records the real client
    // IP + user agent, keyed to the ID token.
    cred.user
      .getIdToken()
      .then((idToken) =>
        fetch(
          `${FUNCTIONS_BASE}/logLoginSuccess`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({}),
          }
        ).catch(() => {})
      )
      .catch(() => {});

    // Step 2: Get role from Firestore
    const roleDoc = await getDoc(doc(db, "user_roles", email.toLowerCase()));
    if (!roleDoc.exists()) {
      setError("Your email is not assigned any access in the portal.");
      setLoading(false);
      return;
    }

    // Derive multi-tenant access; set the active company/site to the user's
    // primary, and pick the role we'll route by after OTP.
    const access = deriveAccess(normalizeMembership(roleDoc.data()));
    if (access.primary) {
      setActiveTenantSite(
        access.primary.tenantId,
        access.primary.siteId || undefined
      );
    }
    const userRole = access.isOwner
      ? "owner"
      : access.primary?.role || roleDoc.data().role || "";
    setRole(userRole);

    // Check the password-rotation clock. Fail OPEN: if there's no record (e.g.
    // a user who predates this feature and hasn't been backfilled), we never
    // warn or block — nobody gets locked out by a missing clock.
    try {
      const secSnap = await getDoc(
        doc(db, "user_security", email.toLowerCase())
      );
      const sec = secSnap.exists() ? secSnap.data() : null;
      if (sec?.passwordChangedAtMs) {
        const ageDays = (Date.now() - sec.passwordChangedAtMs) / 86400000;
        if (ageDays >= 90) {
          setPwdExpired(true);
        } else if (ageDays >= 83) {
          setPwdWarnDays(Math.max(1, Math.ceil(90 - ageDays)));
        }
      }
    } catch (_) {
      /* ignore — rotation check must never block login */
    }

    // Step 3: Trigger OTP — authenticated by the Firebase ID token so the
    // server binds the code to this user (no email in the body).
    const idToken = await cred.user.getIdToken();
    const resp = await fetch(
      `${FUNCTIONS_BASE}/sendOtp`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      }
    );

    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      setError(data.error || "Couldn't send your verification code. Try again.");
      setLoading(false);
      return;
    }

    setShowOtp(true);
  } catch (err) {
    console.error(err);
    setError("Invalid email or password.");
    // Fire-and-forget audit of the failed attempt. Never blocks or surfaces
    // in the UI — the server records IP + user agent and rate-limits per IP.
    try {
      fetch(
        `${FUNCTIONS_BASE}/logFailedLogin`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: (email || "").trim().toLowerCase(),
            reason: err?.code || "auth_failed",
          }),
        }
      ).catch(() => {});
    } catch (_) {
      /* ignore — auditing must never affect login */
    }
  } finally {
    setLoading(false);
  }
};

  const handleVerifyOtp = async () => {
  setError("");
  setLoading(true);

  try {
    const idToken = await auth.currentUser.getIdToken();
    const res = await fetch(
      `${FUNCTIONS_BASE}/verifyOtp`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ otp }),
      }
    );

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.error || "Invalid or expired code.");
      setLoading(false);
      return;
    }

    // OTP verified — the server has written otp_sessions/{uid}.
    // If the password is expired (>=90 days), force a change before entry;
    // ChangePasswordPage sends them on to their role home afterward.
    if (pwdExpired) {
      navigate("/change-password", {
        state: { forced: true, from: roleHome(role) },
      });
      return;
    }
    navigate(roleHome(role));
  } catch (err) {
    console.error(err);
    setError("Verification failed.");
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope] flex flex-col">
      {/* Full-page background with layered gradients & glow */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="/bg_ai.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.28),_transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(37,99,235,0.22),_transparent_55%)]" />
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
        <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
        <div className="absolute -bottom-32 left-1/3 w-[480px] h-[480px] rounded-full bg-fuchsia-600/15 blur-[140px]" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {/* Brand header */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl blur-md opacity-70" />
              <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center overflow-hidden p-1.5">
                <img
                  src="/RegX zoom.png"
                  alt="RegX"
                  className="w-full h-full object-contain scale-110"
                />
              </div>
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/80 font-semibold leading-tight">
                Lung Innovations
              </div>
              <h1 className="text-xl font-bold tracking-tight text-white leading-tight">
                Screening Portal
              </h1>
            </div>
          </div>

          {/* Divider tagline */}
          <div className="flex items-center justify-center gap-2 mb-5">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-white/15" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-medium flex items-center gap-1.5">
              {!UI_REVAMP_ENABLED && <ShieldCheck className="w-3 h-3 text-purple-300" />}
              Authorized Access · Internal Staff
            </span>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-white/15" />
          </div>

          {/* Error banner */}
          {error && (
            <div className="mb-3 rounded-xl border border-rose-400/30 bg-rose-500/10 backdrop-blur-md px-3 py-2 text-sm text-rose-200 flex items-start gap-2 shadow-[0_8px_24px_-12px_rgba(244,63,94,0.5)]">
              <AlertCircle className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Glass auth card */}
          <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_20px_60px_-20px_rgba(124,58,237,0.5)] overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10 pointer-events-none" />
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70" />

            <div className="relative p-5">
              {!showOtp ? (
                // ---------------------- LOGIN FORM ----------------------
                <form onSubmit={handleLogin} className="space-y-3.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold">
                      Sign In
                    </span>
                    <span className="h-px flex-1 bg-white/10" />
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                      <input
                        type="email"
                        placeholder="you@example.com"
                        className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                      <input
                        type="password"
                        placeholder="••••••••"
                        className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative w-full py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white text-sm font-semibold ring-1 ring-white/20 hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    <span className="relative flex items-center justify-center gap-2">
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Sign In
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </span>
                  </button>
                </form>
              ) : (
                // ---------------------- OTP FORM ----------------------
                <div className="space-y-3.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold">
                      Verify Identity
                    </span>
                    <span className="h-px flex-1 bg-white/10" />
                  </div>

                  {pwdWarnDays != null && (
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-500/10 border border-amber-400/30 text-amber-100">
                      <AlertCircle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                      <p className="text-xs leading-relaxed">
                        Your password expires in{" "}
                        <strong>{pwdWarnDays} day{pwdWarnDays === 1 ? "" : "s"}</strong>.
                        After signing in, update it from{" "}
                        <strong>Change Password</strong> to avoid being locked out.
                      </p>
                    </div>
                  )}

                  <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10">
                    <ShieldCheck className="w-4 h-4 text-purple-300 shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-300 leading-relaxed">
                      Enter the 6-digit verification code sent to{" "}
                      <strong className="text-white">{email}</strong>
                    </p>
                  </div>

                  <input
                    type="text"
                    maxLength={6}
                    placeholder="••••••"
                    className="w-full text-center px-3 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-2xl tracking-[0.5em] font-mono text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />

                  <button
                    onClick={handleVerifyOtp}
                    disabled={loading}
                    className="group relative w-full py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white text-sm font-semibold ring-1 ring-white/20 hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    <span className="relative flex items-center justify-center gap-2">
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          Verify Code
                          <ShieldCheck className="w-4 h-4" />
                        </>
                      )}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sub line */}
          <p className="mt-4 text-center text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
            {!UI_REVAMP_ENABLED && <Sparkles className="w-3 h-3 text-purple-300/70" />}
            Two-factor authentication required
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative pb-4 px-4 text-center">
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
          © 2025–{currentYear} Lung Innovations Network · All Rights Reserved
        </p>
        <p className="mt-1 text-[10px] tracking-[0.2em] text-slate-600">
          v2.0 / 31MAY2026
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;
